import type { HelpArticle, HelpCategory } from "@/lib/registrant-help/types";
import {
  STAFF_HELP_CATEGORIES,
  STAFF_SHARED_HELP_ARTICLES,
} from "@/lib/staff-help/shared-articles";

export const GOVERNMENT_HELP_ARTICLE_IDS = [
  "getting-started",
  "dashboard",
  "registrations",
  "needs-attention",
  "compliance-issues",
  "registration-details",
  "registration-upload",
  "parties",
  "party-exempt",
  "prefiling-deregistration-approval",
] as const;

export type GovernmentHelpArticleId =
  (typeof GOVERNMENT_HELP_ARTICLE_IDS)[number];

export const GOVERNMENT_HELP_CATEGORIES: HelpCategory[] = STAFF_HELP_CATEGORIES;

const GOVERNMENT_PORTAL_ARTICLES: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "A tour of the government portal: sign-in, overview, registrations, and Help.",
    category: "getting-started",
    keywords: [
      "overview",
      "portal",
      "start",
      "home",
      "hera",
      "government",
      "help",
    ],
    related: ["dashboard", "registrations", "compliance-issues"],
    sections: [
      {
        heading: "What this portal is for",
        blocks: [
          {
            type: "p",
            text: "The government portal is where jurisdiction staff review registrations, cases, inspections, ordinances, and reports for their communities. You can flag compliance issues, clear issues your portal raised, and work registrations in your scope.",
          },
        ],
      },
      {
        heading: "Finding help",
        blocks: [
          {
            type: "p",
            text: "Use the Help icon in the header, or Help in your account menu, to open this Help Center. Many screens and dialogs also have a Help button that opens the matching article without leaving the page. Help stays inside the government portal so you remain signed in with your government login.",
          },
        ],
      },
      {
        heading: "The main areas",
        blocks: [
          {
            type: "ul",
            items: [
              "Overview — summary dashboards for your jurisdictions.",
              "Registrations — list and map of properties, including the Needs Attention column.",
              "Cases, inspections, ordinances, reports, and setup — jurisdiction tools for your role.",
              "Help — this help center.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    title: "Overview and dashboard",
    summary:
      "How the government overview and registration lists relate to Needs Attention and compliance.",
    category: "getting-started",
    keywords: ["dashboard", "overview", "needs attention", "tiles"],
    related: ["registrations", "needs-attention", "compliance-issues"],
    sections: [
      {
        heading: "Overview",
        blocks: [
          {
            type: "p",
            text: "Overview pages summarize activity for your jurisdictions. Open Registrations to work individual properties, filter by Needs Attention, and open details.",
          },
        ],
      },
      {
        heading: "Needs Attention on the list",
        blocks: [
          {
            type: "p",
            text: "Use the Needs Attention column and filters on Registrations to find late renewals (30 to 90 days, or 30 or more days when Historical Data Cleaned is on for that jurisdiction and license type) and open compliance issues, including Ready for Check. See Needs Attention and Compliance issues for details.",
          },
        ],
      },
    ],
  },
];

export const GOVERNMENT_HELP_ARTICLES: HelpArticle[] = [
  ...GOVERNMENT_PORTAL_ARTICLES,
  ...STAFF_SHARED_HELP_ARTICLES,
];

const ARTICLES_BY_ID = new Map(
  GOVERNMENT_HELP_ARTICLES.map((article) => [article.id, article]),
);

export function getGovernmentHelpArticle(id: string): HelpArticle | undefined {
  return ARTICLES_BY_ID.get(id);
}

export function isGovernmentHelpArticleId(
  id: string,
): id is GovernmentHelpArticleId {
  return ARTICLES_BY_ID.has(id);
}

export function governmentArticlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return GOVERNMENT_HELP_ARTICLES.filter(
    (article) => article.category === categoryId,
  );
}
